// ===================================================
// embeddingApi.js — 임베딩 기반 컨텍스트 검색 (C2)
//
// 역할:
//   - generateEmbedding(text)
//       텍스트를 1536차원 벡터로 변환합니다 (/api/embed Edge Function 경유)
//       동일 텍스트는 인메모리 캐시에서 즉시 반환합니다 (최대 20개 LRU 캐시)
//   - searchContextByEmbedding(text, maxCount, preferMode)
//       현재 입력과 의미적으로 유사한 과거 기록을 검색합니다.
//       Supabase pgvector의 match_sessions RPC 함수를 사용합니다.
//       실패 시 null 반환 → 호출부에서 키워드 매칭 폴백
//
// 기존 방식(키워드 교집합)과의 차이:
//   - 기존: "퇴사"와 "이직" 같은 의미가 통하는 단어도 키워드가 다르면 미검색
//   - 신규: 의미 유사도 기반이므로 "퇴사 고민"을 검색하면 "커리어 전환" 기록도 검색됨
// ===================================================

import { supabase } from '../supabase';
import { getAuthToken } from './gptClient';

// ── 임베딩 인메모리 캐시 (최대 20개 LRU) ─────────────────────────
// 같은 텍스트를 여러 번 입력해도 API를 한 번만 호출합니다.
// LRU(Least Recently Used): 가장 오래 전에 쓴 항목부터 삭제합니다.
const CACHE_MAX = 20;
const embeddingCache = new Map();

function cacheGet(key) {
  if (!embeddingCache.has(key)) return null;
  // LRU 갱신: 기존 항목을 삭제하고 맨 뒤에 다시 삽입
  const value = embeddingCache.get(key);
  embeddingCache.delete(key);
  embeddingCache.set(key, value);
  return value;
}

function cacheSet(key, value) {
  if (embeddingCache.has(key)) embeddingCache.delete(key);
  if (embeddingCache.size >= CACHE_MAX) {
    // 가장 오래된 항목(Map의 첫 번째 키) 삭제
    embeddingCache.delete(embeddingCache.keys().next().value);
  }
  embeddingCache.set(key, value);
}

// 모드명 한국어 표기 (contextMatcher.js와 동일)
const MODE_LABELS = {
  diary:    '다이어리',
  note:     '노트',
  planner:  '플래너',
  study:    '학습',
  decision: '의사결정',
};

// ── 텍스트 → 임베딩 벡터 변환 ──────────────────────────────────
// /api/embed Edge Function을 통해 OpenAI text-embedding-3-small 모델을 호출합니다.
// 반환: 1536개의 숫자로 이루어진 배열 (float[])
// 캐시: 동일 텍스트(최대 200자 기준)는 인메모리 캐시에서 즉시 반환합니다.
export async function generateEmbedding(text) {
  // 캐시 키는 앞 200자로 충분 (같은 내용의 변형 방지)
  const cacheKey = text.slice(0, 200);
  const cached = cacheGet(cacheKey);
  if (cached) return cached;

  const token = await getAuthToken();
  if (!token) throw new Error('로그인이 필요합니다.');

  // 3초 타임아웃: /api/embed Edge Function이 응답하지 않으면 중단
  // (findPastContext의 5초 전체 타임아웃 내에서 RPC까지 여유를 두기 위해 3초로 설정)
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 3000);

  let response;
  try {
    response = await fetch('/api/embed', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    throw new Error('임베딩 요청 타임아웃');
  }
  clearTimeout(timer);

  if (!response.ok) throw new Error('임베딩 생성 실패');

  const { embedding, error } = await response.json();
  if (error) throw new Error(error);

  cacheSet(cacheKey, embedding);
  return embedding;
}

// ── 의미 유사도 기반 과거 기록 검색 ──────────────────────────────
// 현재 텍스트를 벡터화한 뒤, Supabase pgvector로 유사한 과거 기록을 검색합니다.
//
// 반환:
//   - [{ date, mode, summary }, ...] — 검색 결과 (askGPT의 pastContext 형식과 동일)
//   - null — 임베딩 생성 실패 또는 DB 오류 (호출부에서 키워드 매칭으로 폴백)
export async function searchContextByEmbedding(text, maxCount = 3, preferMode = null) {
  // preferMode가 있을 때 조금 더 많이 가져와서 해당 모드 기록을 앞으로 정렬
  const fetchCount = preferMode ? maxCount + 3 : maxCount;

  // 텍스트 → 벡터
  let embedding;
  try {
    embedding = await generateEmbedding(text);
  } catch {
    // 임베딩 생성 실패 → 키워드 매칭으로 폴백
    return null;
  }

  // pgvector 유사도 검색 (match_sessions RPC)
  // 유사도 임계값 0.3 이상인 기록만 반환 (0~1 사이 값, 높을수록 유사)
  // 3초 타임아웃: Supabase가 응답하지 않으면 키워드 매칭으로 폴백합니다.
  let data;
  try {
    const rpcPromise = supabase.rpc('match_sessions', {
      query_embedding: embedding,
      match_threshold: 0.3,
      match_count: fetchCount,
    });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('RPC timeout')), 3000)
    );
    const { data: rows, error } = await Promise.race([rpcPromise, timeoutPromise]);
    if (error) return null;
    data = rows;
  } catch {
    return null;
  }

  if (!data || data.length === 0) return null;

  // preferMode가 있으면 해당 모드의 기록을 앞으로 정렬 (동일 유사도 시 우선 노출)
  const sorted = preferMode
    ? [...data].sort((a, b) => {
        const aMatch = a.mode === preferMode ? 1 : 0;
        const bMatch = b.mode === preferMode ? 1 : 0;
        return bMatch - aMatch;
      }).slice(0, maxCount)
    : data.slice(0, maxCount);

  return sorted.map(row => ({
    date:    formatRelativeDate(row.date),
    mode:    MODE_LABELS[row.mode] || row.mode,
    summary: row.context_summary,
  }));
}

// 날짜 문자열 "YYYY-MM-DD"를 상대적 표현("3일 전" 등)으로 변환
// contextMatcher.js의 동일 함수와 동작 통일
function formatRelativeDate(dateStr) {
  const target = new Date(dateStr);
  const today  = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return '오늘';
  if (diffDays === 1) return '어제';
  if (diffDays < 7)   return `${diffDays}일 전`;
  if (diffDays < 14)  return '1주 전';
  if (diffDays < 30)  return `${Math.floor(diffDays / 7)}주 전`;
  return `${Math.floor(diffDays / 30)}달 전`;
}
